import { useEffect, useState } from 'react';
import { aiApi } from '../services/api/aiApi';
import type { ComposeEnvelope, ComposeMetadata, RecapDraftOutput } from '../services/api/aiTypes';

/**
 * MED-893 AC7/AC8 — fetch the AI recap-email draft envelope once on mount.
 *
 * Fires when both `sessionId` and `enabled` (the `live_session_delivery` flag)
 * are truthy. The output feeds:
 *   - SendRecapEmailModal initial subject/body (AC8)
 *   - MedleyFeedbackFormCard `session_insights` + `carryover_for_next_session`
 *     pre-population (AC7; `session_insights` re-targeted from `session_themes`
 *     in MED-988)
 *
 * `fail_open` + any unexpected error are surfaced as the same envelope shape
 * with `result: null`, so callers branch only on `envelope.result`. The coach
 * is never blocked by AI failure (per AC8 contract).
 */

export interface UseRecapDraftResult {
  envelope: ComposeEnvelope<RecapDraftOutput> | null;
  loading: boolean;
}

function localFailOpenEnvelope(): ComposeEnvelope<RecapDraftOutput> {
  const metadata: ComposeMetadata = {
    recipe_version: 'unwired',
    generated_at: new Date().toISOString(),
    latency_ms: 0,
    is_mock: false,
    judge_score: null,
    judge_critical_issues: [],
    request_id: 'client-catch-fail-open',
  };
  return { status: 'fail_open', result: null, metadata };
}

export function useRecapDraft(
  sessionId: string | null,
  enabled: boolean,
): UseRecapDraftResult {
  const [envelope, setEnvelope] = useState<ComposeEnvelope<RecapDraftOutput> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !sessionId) {
      setEnvelope(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    aiApi
      .recapDraft({ session_id: sessionId })
      .then((env) => {
        if (!cancelled) setEnvelope(env);
      })
      .catch(() => {
        if (!cancelled) setEnvelope(localFailOpenEnvelope());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, enabled]);

  return { envelope, loading };
}

export default useRecapDraft;
